import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Trans } from 'services/i18n';
import { deleteListsAndFoldersMutation } from 'api/mutations';
import { FolderId } from 'modules/domains/foldering';
import { ListId, useTranslation } from 'modules/domains/list';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import noop from 'utils/noop';
import { CurrentSelection, SelectionTypes } from 'utils/contexts/selection';

export enum KnownDeleteFailures {
  CannotDeleteRootFolderFailure = 'CannotDeleteRootFolderFailure',
  FolderDoesNotExistFailure = 'FolderDoesNotExistFailure',
}

export type DeleteError = {
  id: FolderId | ListId;
  name?: string;
  __typename: KnownDeleteFailures;
};

export enum DeleteSuccessTypenames {
  folder = 'FolderMutationSuccess',
  content = 'ContentItemSuccess',
}

export type DeleteFolderSuccess = {
  id: FolderId;
  name: string;
  __typename: DeleteSuccessTypenames.folder;
};

export type DeleteListSuccess = {
  id: ListId;
  name: string;
  __typename: DeleteSuccessTypenames.content;
};

export type DeleteListsAndFoldersResponseData = {
  deleteListContentsAndFolders: {
    folderFailures: DeleteError[];
    deletedContent: (DeleteFolderSuccess | DeleteListSuccess)[];
  };
};

export type Variables = {
  contentIds: ListId[];
  folderIds: FolderId[];
};

export type Config = {
  onComplete: () => void;
  onError: () => void;
  onSuccess: () => void;
  itemsById: Record<FolderId | ListId, CurrentSelection<ListId>>;
};

export function useDeleteListsAndFolders({
  onComplete = noop,
  onError = noop,
  onSuccess = noop,
  itemsById = {},
}: Config) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [deleteListContentsAndFolders, data] = useMutation<
    DeleteListsAndFoldersResponseData,
    Variables
  >(deleteListsAndFoldersMutation);

  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!data.called) {
      return;
    }

    if (!data.loading && data.error) {
      dispatch(
        addNegativeToast(t('indexPage.toasts.delete.deleteListContentsAndFolder.error.unknown')),
      );
    }

    if (!data.loading && data?.data?.deleteListContentsAndFolders) {
      const { folderFailures, deletedContent } = data.data.deleteListContentsAndFolders;

      folderFailures.forEach((err: DeleteError) => {
        switch (err.__typename) {
          case KnownDeleteFailures.CannotDeleteRootFolderFailure: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  {t('indexPage.toasts.delete.deleteListContentsAndFolder.error.root')}
                </ResponseWrapper>,
              ),
            );
            break;
          }

          case KnownDeleteFailures.FolderDoesNotExistFailure: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.deleteListContentsAndFolder.error.doesNotExist"
                    components={{
                      namedFolder: <NamedFolder name={itemsById[err.id].name} />,
                    }}
                  >
                    <NamedFolder name={itemsById[err.id].name} /> folder can not be deleted. It does
                    not exist.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            dispatch(
              addNegativeToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.deleteListContentsAndFolder.error.default"
                    components={{
                      namedFolder: <NamedFolder name={itemsById[err.id].name} />,
                    }}
                  >
                    <NamedFolder name={itemsById[err.id].name} /> folder could not be deleted.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            console.error(`Unexpected Error Type (${err.__typename})`); // eslint-disable-line
          }
        }
        onError();
      });

      deletedContent.forEach((content: DeleteFolderSuccess | DeleteListSuccess) => {
        const contentType = itemsById[content.id]?.type;
        switch (contentType) {
          case SelectionTypes.Folder: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.deleteListContentsAndFolder.success.onListOrFolder"
                    components={{
                      namedFolder: <NamedFolder name={content.name} />,
                    }}
                  >
                    <NamedFolder name={content.name} /> was successfully delete.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            /* List confirmations should be shown as default because itemsById WILL NOT include any
             * nested folders/lists that may be deleted/archived. However, the response WILL return deleted
             * nested lists { id, name }, but not any nested folders */
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.deleteListContentsAndFolder.success"
                    components={{
                      strong: <strong />,
                    }}
                    values={{
                      name: content.name,
                    }}
                  >
                    <NamedFolder name={content.name} /> was successfully deleted.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            break;
          }
        }

        onSuccess();
      });

      // call the complete callback
      onComplete();
    }
  }, [data.loading]);

  return [deleteListContentsAndFolders, data] as const;
}
